import { useRef, createRef } from 'react';

import Heading from 'components/common/Heading';
import Subheading from 'components/common/Subheading';
import Text from 'components/common/Text';
import ToggleButton from 'components/common/ToggleButton';
import SpoonAndFork from 'images/reviewCardGallery/SpoonAndFork.svg';
import { STANDARD_SERVINGS_PER_MEAL, HOME_CHEF_MEALS_PER_WEEK } from 'lib/constants';

const StandardBoxCustomizer = ({
  servings,
  weeklyMeals,
  setServings,
  setWeeklyMeals,
  potentialSavingsText = '',
  boxRecommendations,
}) => {
  // For Keyboard nav accessibility
  const servingsRefs = useRef(STANDARD_SERVINGS_PER_MEAL.map(() => createRef()));
  const weeklyMealRefs = useRef(HOME_CHEF_MEALS_PER_WEEK.map(() => createRef()));

  const containerText = {
    header: 'Recommended Box Size',
    subHeader: 'Your recommendation is for the number of lunches and/or dinners we’ll suggest.',
    question: 'Number of Servings Per Recipe',
    subtext: 'Each serving feeds an individual',
    subQuestion: 'Number of Recipes Per Week',
  };

  return (
    <>
      <Heading className="mb-6 text-3xl bp2:mb-4">{containerText.header}</Heading>
      {containerText?.subHeader && (
        <Subheading className="text mb-6 text-center">{containerText?.subHeader}</Subheading>
      )}
      <section>
        <Subheading
          className="mb-2 max-w-md text-left text-lg font-semibold"
          brandStylesOverrides="text-secondary h6"
        >
          {containerText.question}
        </Subheading>
        <Text className="textSm mb-4 block text-left">{containerText.subtext}</Text>
        <div className="mb-8 flex flex-row text-center">
          <div
            className="inline-flex flex-wrap gap-4"
            role="radiogroup"
            aria-label="Servings options"
          >
            {STANDARD_SERVINGS_PER_MEAL.map((numberOfServings, index) => (
              <ToggleButton
                id={numberOfServings}
                ref={servingsRefs.current[index]}
                selected={numberOfServings === servings}
                handleClick={() => setServings(numberOfServings)}
                className="relative h-14 w-24 p-1 px-6 bp1:h-14 bp2:w-36"
                type="preference"
                key={numberOfServings}
                aria-label={`${numberOfServings} servings${numberOfServings === boxRecommendations.recommendedServings ? ', BEST FIT' : ''}`}
                role="radio"
                aria-checked={numberOfServings === servings}
                tabIndex={numberOfServings === servings ? 0 : -1}
                refs={servingsRefs.current}
                setSelected={setServings}
              >
                {numberOfServings === boxRecommendations.recommendedServings && (
                  <span className="h7 absolute bottom-10 left-0 right-0 w-16 rounded-2xl border border-solid border-lemon-100 bg-keto-100 text-charcoal-100 bp1:left-4 bp1:py-1">
                    BEST FIT
                  </span>
                )}
                {numberOfServings}
              </ToggleButton>
            ))}
          </div>
        </div>
      </section>

      <section>
        <Subheading className="mb-4 max-w-md text-left text-lg font-semibold">
          {containerText.subQuestion}
        </Subheading>
        <div className="mb-8 flex flex-row text-center">
          <div className="grid grid-cols-3 gap-4" role="radiogroup" aria-label="Recipes options">
            {HOME_CHEF_MEALS_PER_WEEK.map((numberOfWeeklyMeals, index) => (
              <ToggleButton
                id={numberOfWeeklyMeals}
                ref={weeklyMealRefs.current[index]}
                selected={numberOfWeeklyMeals === weeklyMeals}
                handleClick={() => setWeeklyMeals(numberOfWeeklyMeals)}
                className="relative h-14 w-24 p-1 px-6 bp1:h-14 bp2:w-36"
                type="preference"
                key={numberOfWeeklyMeals}
                aria-label={`${numberOfWeeklyMeals} recipes${numberOfWeeklyMeals === boxRecommendations.recommendedMeals ? ', BEST FIT' : ''}`}
                role="radio"
                aria-checked={numberOfWeeklyMeals === weeklyMeals}
                tabIndex={numberOfWeeklyMeals === weeklyMeals ? 0 : -1}
                refs={weeklyMealRefs.current}
                setSelected={setWeeklyMeals}
              >
                {numberOfWeeklyMeals === boxRecommendations.recommendedMeals && (
                  <span className="h7 absolute bottom-10 left-0 right-0 w-16 rounded-2xl border border-solid border-lemon-100 bg-keto-100 text-charcoal-100 bp1:left-4 bp1:py-1">
                    BEST FIT
                  </span>
                )}
                {numberOfWeeklyMeals}
              </ToggleButton>
            ))}
          </div>
        </div>
        {potentialSavingsText && (
          <div className="mb-8 flex flex-row items-center pl-2">
            <img src={SpoonAndFork} alt="SpoonAndFork" className="pr-4" />
            <Text brandStylesOverrides="text text-light" className="text-left">
              {potentialSavingsText}
            </Text>
          </div>
        )}
      </section>
    </>
  );
};

StandardBoxCustomizer.propTypes = {
  servings: PropTypes.number.isRequired,
  weeklyMeals: PropTypes.number.isRequired,
  setServings: PropTypes.func.isRequired,
  setWeeklyMeals: PropTypes.func.isRequired,
  potentialSavingsText: PropTypes.string,
  boxRecommendations: PropTypes.object.isRequired,
};

export default StandardBoxCustomizer;
